import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  tokens,
  fmtNumber,
  SignalBar,
  StatusPill,
  ClinicalDataGrid,
  ClinicalPageShell,
  SectionPanel,
} from "../../shared/clinical-design";
import NoteReviewDetail from "../components/NoteReviewDetail";
import {
  getNotesWithFindings,
  getProviderScores,
  sendCoaching,
  buildCoachSignals,
} from "../services/providerDocCoachService";
import { NOTE_STATUS, NOTE_STATUS_META } from "../common/constants";

const scoreColor = (v) => (v >= 80 ? tokens.ok : v >= 65 ? tokens.warn : tokens.crit);

/**
 * Provider Documentation Coach (Módulo F) — master-detail:
 * izquierda cola de notas con hallazgos, derecha revisión + coaching.
 * Abajo: scorecard de calidad documental por provider.
 */
export default function ProviderDocCoachPage() {
  const [notes, setNotes] = useState([]);
  const [providerScores, setProviderScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let active = true;
    Promise.all([getNotesWithFindings(), getProviderScores()]).then(([n, p]) => {
      if (!active) return;
      setNotes(n);
      setProviderScores(p);
      setSelectedId(n[0]?.id ?? null);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  const selected = useMemo(
    () => notes.find((n) => n.id === selectedId) || null,
    [notes, selectedId]
  );

  const signals = useMemo(() => {
    const s = buildCoachSignals(notes, providerScores);
    return [
      { label: "Flagged notes", value: fmtNumber(s.flaggedNotes), tone: "info" },
      { label: "Pending review", value: fmtNumber(s.pendingReview), tone: s.pendingReview > 0 ? "warn" : "ok" },
      { label: "Specificity score", value: `${s.specificity}%`, tone: s.specificity >= 80 ? "ok" : "warn", hint: "Average across providers, current month" },
      { label: "MEAT completeness", value: `${s.meat}%`, tone: s.meat >= 80 ? "ok" : "warn" },
      { label: "Missed HCC (month)", value: fmtNumber(s.missedHcc), tone: "crit", hint: "HCC opportunities detected in closed notes but not documented" },
    ];
  }, [notes, providerScores]);

  const handleSendCoaching = async (noteId) => {
    setSending(true);
    // Optimistic UI — cambio solo local hasta que exista
    // PUT /api/analytics/doc-feedback/{id}/send-coaching en el backend.
    setNotes((prev) =>
      prev.map((n) => (n.id === noteId ? { ...n, status: NOTE_STATUS.COACHED } : n))
    );
    await sendCoaching(noteId);
    setSending(false);
  };

  const noteColumns = [
    { key: "noteDate", header: "Date", mono: true, width: 90 },
    { key: "provider", header: "Provider" },
    {
      key: "patientName",
      header: "Patient",
      render: (row) => (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: tokens.ink, lineHeight: 1.3 }}>
            {row.patientName}
          </Typography>
          <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 11.5, color: tokens.ink3 }}>
            {row.noteType}
          </Typography>
        </Box>
      ),
    },
    {
      key: "issues",
      header: "Issues",
      align: "right",
      mono: true,
      render: (row) => (row.issues.length ? `${row.issues.length}` : "—"),
    },
    {
      key: "docScore",
      header: "Score",
      align: "right",
      render: (row) => (
        <Typography
          component="span"
          sx={{
            fontFamily: tokens.fontMono,
            fontVariantNumeric: "tabular-nums",
            fontSize: 13,
            color: scoreColor(row.docScore),
          }}
        >
          {row.docScore}
        </Typography>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => {
        const meta = NOTE_STATUS_META[row.status];
        return <StatusPill tone={meta.tone} label={meta.label} dense />;
      },
    },
  ];

  const providerColumns = [
    { key: "provider", header: "Provider" },
    { key: "notesReviewed", header: "Notes reviewed", align: "right", mono: true },
    {
      key: "specificity",
      header: "Specificity",
      align: "right",
      render: (row) => (
        <Typography component="span" sx={{ fontFamily: tokens.fontMono, fontSize: 13, color: scoreColor(row.specificity) }}>
          {row.specificity}%
        </Typography>
      ),
    },
    {
      key: "meat",
      header: "MEAT",
      align: "right",
      render: (row) => (
        <Typography component="span" sx={{ fontFamily: tokens.fontMono, fontSize: 13, color: scoreColor(row.meat) }}>
          {row.meat}%
        </Typography>
      ),
    },
    { key: "missedHcc", header: "Missed HCC", align: "right", mono: true },
    { key: "trend", header: "Trend", align: "right", mono: true },
  ];

  return (
    <ClinicalPageShell
      title="Provider Documentation Coach"
      subtitle="Educational (non-punitive) feedback on closed notes: specificity, MEAT completeness and missed HCC documentation."
      moduleTag="Module F"
      devPreview
    >
      <SignalBar signals={signals} sx={{ mb: 1.5 }} />

      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: "wrap" }}>
        <Box sx={{ flex: "0 1 45%", minWidth: 380 }}>
          <ClinicalDataGrid
            columns={noteColumns}
            rows={notes}
            rowKey={(row) => row.id}
            loading={loading}
            onRowClick={(row) => setSelectedId(row.id)}
            isRowSelected={(row) => row.id === selectedId}
            emptyMessage="No notes with documentation findings in the current period."
            maxHeight={560}
          />
        </Box>
        <Box sx={{ flex: "1 1 480px", minWidth: 380 }}>
          <NoteReviewDetail note={selected} onSendCoaching={handleSendCoaching} sending={sending} />
        </Box>
      </Box>

      <SectionPanel title="Provider documentation quality — current month" sx={{ mt: 1.5 }} contentSx={{ p: 0 }}>
        <ClinicalDataGrid
          columns={providerColumns}
          rows={providerScores}
          rowKey={(row) => row.provider}
          loading={loading}
          emptyMessage="No provider scores available yet."
          maxHeight={280}
          sx={{ border: 0 }}
        />
      </SectionPanel>
    </ClinicalPageShell>
  );
}
